/**
 * <s-product-image-zoom> — PDP full-screen photo viewer.
 *
 * Gallery buttons open one shared native dialog declaratively via commandfor/show-modal.
 * This component:
 *   - remembers which photo was clicked and opens the viewer scrolled to it,
 *   - drives the viewer's previous/next arrows, arrow keys and "n / total" counter,
 *   - warms the matching high-resolution image on hover/focus.
 * Swiping works natively through the scroll-snap track.
 *
 * Required markup:
 *   [data-zoom-preload][commandfor][data-zoom-index] — gallery trigger button; commandfor points to the dialog id.
 *   dialog [data-zoom-track]                         — horizontal scroll-snap track, one full-width slide per photo.
 *   dialog img[data-zoom-image]                      — high-resolution image in each slide, in track order.
 * Optional markup:
 *   dialog [data-zoom-prev] / [data-zoom-next]       — arrow buttons.
 *   dialog [data-zoom-current]                       — element whose text is the 1-based current photo number.
 */
import { EndlessTrack } from '@theme/endless-track';

// On phones the viewer loops endlessly: the last photo swipes on to the first, and back.
const PHONE_QUERY = '(max-width: 767px)';

class SProductImageZoom extends HTMLElement {
  #loop;
  #phoneQuery = window.matchMedia(PHONE_QUERY);
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];
  #pendingIndex = 0;
  #scrollRaf = 0;
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);
    this.addEventListener('click', this.#handleClick, opts);

    const dialog = this.#dialog;
    const track = this.#track;
    if (dialog && track) {
      dialog.addEventListener('toggle', this.#handleToggle, opts);
      dialog.addEventListener('keydown', this.#handleKeydown, opts);
      track.addEventListener('scroll', this.#handleScroll, { ...opts, passive: true });
      this.#loop = new EndlessTrack(track, track.children, { count: 1, onSettle: (index) => this.#updateControls(index) });
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#loop?.destroy();
    cancelAnimationFrame(this.#scrollRaf);

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }

  get #dialog() {
    return this.querySelector('dialog');
  }

  get #track() {
    return this.querySelector('[data-zoom-track]');
  }

  get #images() {
    return Array.from(this.querySelectorAll('img[data-zoom-image]'));
  }

  get #currentIndex() {
    const track = this.#track;
    if (!track || track.clientWidth === 0) return 0;
    if (this.#loop?.enabled) return this.#loop.currentIndex();
    return Math.round(Math.abs(track.scrollLeft) / track.clientWidth);
  }

  #handleClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const trigger = event.target.closest('[data-zoom-preload]');
    if (trigger && this.contains(trigger)) {
      this.#pendingIndex = Number(trigger.dataset.zoomIndex) || 0;
      this.#warmImage(this.#pendingIndex);
      return;
    }

    if (event.target.closest('[data-zoom-prev]')) this.#go(-1);
    else if (event.target.closest('[data-zoom-next]')) this.#go(1);
  };

  #handleToggle = (event) => {
    if (event.newState !== 'open') return;
    if (this.#phoneQuery.matches) this.#loop?.enable(this.#pendingIndex);
    else this.#loop?.disable(this.#pendingIndex);
    this.#scrollTo(this.#pendingIndex, false);
  };

  #handleKeydown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#go(document.dir === 'rtl' ? 1 : -1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#go(document.dir === 'rtl' ? -1 : 1);
    }
  };

  #handleScroll = () => {
    cancelAnimationFrame(this.#scrollRaf);
    this.#scrollRaf = requestAnimationFrame(() => this.#updateControls());
  };

  #go(delta) {
    if (this.#loop?.enabled) {
      const total = this.#images.length;
      this.#warmImage((this.#currentIndex + delta + total) % total);
      this.#loop.scrollToElement(this.#loop.neighbour(delta), !this.#reducedMotion.matches);
      return;
    }
    this.#scrollTo(this.#currentIndex + delta, true);
  }

  #scrollTo(index, animate) {
    const track = this.#track;
    if (!track) return;

    const total = this.#images.length;
    const target = Math.max(0, Math.min(total - 1, index));
    this.#warmImage(target);

    if (this.#loop?.enabled) {
      this.#loop.jumpTo(target);
      this.#updateControls(target);
      return;
    }

    const direction = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
    track.scrollTo({
      left: direction * target * track.clientWidth,
      behavior: animate && !this.#reducedMotion.matches ? 'smooth' : 'instant',
    });
    this.#updateControls(target);
  }

  #updateControls(index = this.#currentIndex) {
    const total = this.#images.length;
    const current = this.querySelector('[data-zoom-current]');
    if (current) current.textContent = String(index + 1);

    const prev = this.querySelector('[data-zoom-prev]');
    const next = this.querySelector('[data-zoom-next]');
    const looping = Boolean(this.#loop?.enabled);
    if (prev) prev.disabled = !looping && index <= 0;
    if (next) next.disabled = !looping && index >= total - 1;
  }

  #warmImage(index) {
    const image = this.#images[index];
    if (!(image instanceof HTMLImageElement)) return;
    image.loading = 'eager';
  }

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    this.#preload(Number(source.dataset.zoomIndex) || 0);
  };

  #preload(index) {
    if (this.#preloaded.has(index)) return;

    const image = this.#images[index];
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(index);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
